/**
 * Feature E06-F04 — Library & History split-view (E06-F04-T02 companies browser,
 * T03 subnav + stat-cards + panel switch, T04 open-by-status routing, T05 clone).
 *
 * 1:1 port of the prototype `../EER app/prototype/assets/research.proto.js`
 * (renderSubnav / renderLibrary / renderHistory / openRequest / cloneRequest).
 * Two entry points behind one switcher:
 *   - Library  → browse EVERY company in the DB + 4 stat-cards (T02/T03);
 *   - History  → past research runs, each row opens where it stands (T04) and can
 *                be cloned into a fresh Intake draft (T05).
 *
 * E06-F05-T11 ([BUG] "Research History без пагинации") — History used to call
 * `useResearchHistory({ page: 1 })` with a hardcoded page and no filters, so only
 * the 20 newest runs were reachable in the UI. On dev that hid BOTH the only
 * `running` and the only `failed` request (positions 39 and 27 of 39), i.e. every
 * run-stage request was unreachable and the AI-check e2e journey had nothing to
 * land on. The panel now owns `page` + `status` + `q` state and drives the SAME
 * server-side query params the Library panel already used — the API
 * (`routes/research-library.ts` → `services/researchLibrary.ts` `listHistory` /
 * `buildHistoryWhere`) already supported `page`/`page_size`/`status`/`q`; only the
 * client never sent them. No API change was needed.
 *
 * All presentation rules (entity badge, pager window, "Showing X–Y of N", status
 * pill, open-by-status target, date formatting) live in `./researchLibraryModel.ts`
 * (unit-tested without a DOM); this component is the thin React shell that composes
 * them with the `./researchLibraryApi.ts` Query hooks. No `innerHTML` —
 * name/domain/criteria are React-escaped.
 *
 * Screen routing (T04/T05): the component owns the Library↔History panel switch
 * internally (parity with the prototype `state.panel`), but delegates opening a
 * past run / a cloned draft to the host via `onOpenScreen(screen, ctx)` so the
 * shell can mount the real Intake / Run / Review screens (which live in other
 * features). `ctx` carries the requestId and, for a clone, the prefilled criteria.
 *
 * Wire-up (the serial Wire pass mounts this as the `research` view body in the
 * shell — see `shell/AppShell.tsx`):
 *   import { ResearchLibraryView } from '../research/ResearchLibraryView.js';
 *   …
 *   {view === 'research' ? <ResearchLibraryView onOpenScreen={openResearchScreen} /> : …}
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';
import type {
  HistoryRequestRow,
  LibraryCompanyRow,
  IntakeCriteria,
  ResearchRequestStatus,
  ResearchScreen,
  ResearchSearchMatch,
  ResearchSearchMatchField,
} from '@eer/shared';
import { RESEARCH_REQUEST_STATUSES, WEBSITE_NOT_FOUND_LABEL } from '@eer/shared';
import {
  entityBadgeText,
  pagerWindow,
  compactPageItems,
  statusPill,
  openTarget,
  openButtonCaption,
  formatDate,
  verifiedLabel,
  industryFilterOptions,
  type PagerWindow,
} from './researchLibraryModel.js';
import { useResearchDictionaries } from '../admin/researchDictionariesApi.js';
import {
  useResearchLibrary,
  useResearchHistory,
  useCloneResearchRequest,
  type LibraryListFilter,
  type HistoryListFilter,
} from './researchLibraryApi.js';
import {
  DEFAULT_BROWSE_STATE,
  sameBrowseState,
  type ResearchBrowseState,
  type ResearchPanel,
} from './researchNavModel.js';

// ---------------------------------------------------------------------------
// public props
// ---------------------------------------------------------------------------

/** Context handed to the host when a row opens a screen (T04/T05). */
export interface OpenScreenContext {
  /** The History row's request id; 0 for a clone (nothing is persisted, E12-F17-T04). */
  readonly requestId: number;
  /** The request's status (drives the run/failed distinction on the Run screen). */
  readonly status?: ResearchRequestStatus;
  /**
   * For a clone: the criteria to pre-fill the Intake form (T05). This is the
   * machine-valued {@link IntakeCriteria} the clone endpoint returns verbatim — the
   * exact shape `research_requests.criteria` stores — so the Intake form consumes it
   * directly (BUG-16: no legacy-blob remap).
   */
  readonly criteria?: IntakeCriteria;
  /** For a clone: the suggested Intake title '<source title> (copy)' (E12-F17-T04). */
  readonly title?: string;
  /** For a clone: the request the criteria were copied from (E12-F17-T04). */
  readonly sourceRequestId?: number;
}

export interface ResearchLibraryViewProps {
  /**
   * Open a past run / cloned draft on the target screen (T04/T05). The shell
   * routes `intake|run|review` to the matching feature screen. Optional — when
   * omitted the component still renders (read-only browser) and the open/clone
   * buttons no-op gracefully.
   */
  readonly onOpenScreen?: (screen: ResearchScreen, ctx: OpenScreenContext) => void;
  /** Optional industry dictionary for the filter dropdown (All + values). */
  readonly industries?: readonly string[];
  /**
   * E06-F02-T11 — the browser state (panel + History search/status/page) to open
   * with. The host keeps it across the flow screens, so coming back from Review /
   * Run lands on the SAME panel with the SAME filtered list, not a reset one.
   * Read on mount only (the view owns the live state afterwards).
   */
  readonly initialBrowse?: ResearchBrowseState;
  /** E06-F02-T11 — reports every browser-state change up to the host. */
  readonly onBrowseChange?: (browse: ResearchBrowseState) => void;
}

/** Debounce a value by `delay` ms (prototype search resets to page 1 on input). */
function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// subnav (T03 acceptance #1/#2) — two entry points + the request-flow breadcrumb
// ---------------------------------------------------------------------------

interface SubnavProps {
  readonly panel: ResearchPanel;
  readonly onPanel: (panel: ResearchPanel) => void;
}

const FLOW_STEPS: ReadonlyArray<{ key: ResearchScreen | 'publish'; label: string }> = [
  { key: 'intake', label: 'New Request' },
  { key: 'run', label: 'Run' },
  { key: 'review', label: 'Review' },
  { key: 'publish', label: 'Publish' },
];

/** Navy/gold active pill (prototype: `bg-slate-900 text-[#D4AF37]`). */
function pillClass(active: boolean): string {
  return active
    ? 'bg-slate-900 text-[#D4AF37] border-slate-900'
    : 'bg-white text-slate-500 hover:text-slate-800 border-gray-200 hover:border-slate-300';
}

function ResearchSubnav({ panel, onPanel }: SubnavProps): JSX.Element {
  return (
    <div
      id="research-subnav"
      data-testid="research-subnav"
      className="flex flex-wrap items-center gap-1 mb-6"
    >
      {/* E06-F04-T08 — History first, Library last (raw material, rarely opened). */}
      <button
        type="button"
        data-testid="subnav-history"
        aria-pressed={panel === 'history'}
        onClick={() => onPanel('history')}
        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition flex items-center gap-1 ${pillClass(
          panel === 'history',
        )}`}
      >
        History
      </button>
      <span className="mx-2 h-5 w-px bg-gray-200" aria-hidden="true" />
      {FLOW_STEPS.map((step, i) => (
        <span key={step.key} className="flex items-center">
          {i > 0 ? (
            <i
              className="fa-solid fa-chevron-right text-gray-300 text-[10px] mx-1"
              aria-hidden="true"
            />
          ) : null}
          <span
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${pillClass(false)}`}
          >
            {step.label}
          </span>
        </span>
      ))}
      <span className="mx-2 h-5 w-px bg-gray-200" aria-hidden="true" />
      <button
        type="button"
        data-testid="subnav-library"
        aria-pressed={panel === 'library'}
        onClick={() => onPanel('library')}
        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition flex items-center gap-1 ${pillClass(
          panel === 'library',
        )}`}
      >
        Library
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// stat cards (T03 acceptance #3)
// ---------------------------------------------------------------------------

interface StatCardProps {
  readonly label: string;
  readonly value: string;
  readonly accent: string;
}

function StatCard({ label, value, accent }: StatCardProps): JSX.Element {
  return (
    <div className={`bg-white p-6 rounded-lg shadow-sm border-l-4 ${accent}`}>
      <div className="text-gray-500 text-xs uppercase font-bold mb-1">{label}</div>
      <div className="text-3xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// shared pager footer (Library T02 + History E06-F05-T11)
// ---------------------------------------------------------------------------

interface ListPagerProps {
  readonly pager: PagerWindow;
  readonly onPage: (page: number) => void;
  /** testid of the "Showing X–Y of N" caption. */
  readonly captionTestId: string;
  /**
   * testid prefix of the buttons: `<prefix>-prev` / `<prefix>-page-<n>` /
   * `<prefix>-next`. The Library panel keeps its historical un-prefixed ids
   * (`pager-*`) so the existing E06-F04-T06 spec selectors stay valid.
   */
  readonly buttonTestIdPrefix: string;
}

/**
 * Prev / page-numbers / next + the "Showing X–Y of N" caption. Extracted from
 * the Library footer by E06-F05-T11 so the History panel reuses the exact same
 * affordance instead of being stuck on a hardcoded page 1.
 */
function ListPager({
  pager,
  onPage,
  captionTestId,
  buttonTestIdPrefix,
}: ListPagerProps): JSX.Element {
  const atFirst = pager.page <= 1;
  const atLast = pager.page >= pager.pages;
  // E06-F04-T09 — compact window (first / last / neighbours + …) instead of one
  // button per page: 246 pages used to overflow the right screen edge.
  const items = compactPageItems(pager.pages, pager.page);
  return (
    <div className="px-4 py-2.5 bg-slate-50 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
      <span data-testid={captionTestId}>
        Showing{' '}
        <b>
          {pager.from}–{pager.to}
        </b>{' '}
        of <b>{pager.total}</b>
      </span>
      <div className="flex flex-wrap items-center gap-1" data-testid={`${buttonTestIdPrefix}-nav`}>
        <button
          type="button"
          data-testid={`${buttonTestIdPrefix}-prev`}
          aria-label="Previous page"
          onClick={() => onPage(Math.max(1, pager.page - 1))}
          disabled={atFirst}
          className={`px-2.5 py-1 rounded border ${
            atFirst
              ? 'border-gray-100 text-gray-300 cursor-not-allowed'
              : 'border-gray-200 text-slate-600 hover:bg-white'
          }`}
        >
          ‹
        </button>
        {items.map((p) =>
          typeof p !== 'number' ? (
            <span
              key={p}
              data-testid={`${buttonTestIdPrefix}-${p}`}
              aria-hidden="true"
              className="px-1 text-slate-400 select-none"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              data-testid={`${buttonTestIdPrefix}-page-${p}`}
              aria-current={p === pager.page ? 'page' : undefined}
              onClick={() => onPage(p)}
              className={`px-2.5 py-1 rounded border ${
                p === pager.page
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'border-gray-200 text-slate-600 hover:bg-white'
              }`}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          data-testid={`${buttonTestIdPrefix}-next`}
          aria-label="Next page"
          onClick={() => onPage(Math.min(pager.pages, pager.page + 1))}
          disabled={atLast}
          className={`px-2.5 py-1 rounded border ${
            atLast
              ? 'border-gray-100 text-gray-300 cursor-not-allowed'
              : 'border-gray-200 text-slate-600 hover:bg-white'
          }`}
        >
          ›
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library panel (T02 + stat-cards)
// ---------------------------------------------------------------------------

interface LibraryPanelProps {
  readonly onGoHistory: () => void;
  readonly onNewRequest: () => void;
  readonly industries: readonly string[];
}

function LibraryRow({ co }: { co: LibraryCompanyRow }): JSX.Element {
  const badge = entityBadgeText(co);
  const verified = verifiedLabel(co.last_verified_at);
  return (
    <tr className="hover:bg-slate-50 transition" data-testid="library-row">
      <td className="px-5 py-3">
        <div className="font-medium text-slate-800 flex items-center">
          <span data-testid="company-name">{co.name}</span>
          {badge ? (
            <span
              data-testid="entity-badge"
              className="ml-1 text-[10px] inline-flex items-center gap-1 text-slate-400"
            >
              {badge}
            </span>
          ) : null}
        </div>
        <div className="text-xs">
          {co.domain ? (
            <a
              href={`https://${co.domain}`}
              target="_blank"
              rel="noopener"
              className="text-blue-600 hover:underline"
              data-testid="company-domain"
            >
              {co.domain}
            </a>
          ) : (
            <span className="italic text-amber-500" data-testid="no-website">
              {WEBSITE_NOT_FOUND_LABEL}
            </span>
          )}
        </div>
      </td>
      <td className="px-5 py-3 text-xs text-slate-600">
        {co.industry ?? '—'}
        <div className="text-gray-400">{co.sub_industry ?? ''}</div>
      </td>
      <td className="px-5 py-3 text-xs text-slate-600">{co.size_band ?? '—'}</td>
      <td className="px-5 py-3 text-xs text-slate-600">{co.hq ?? '—'}</td>
      <td className="px-5 py-3 text-xs text-slate-500 text-center">{co.founded ?? '—'}</td>
      <td className="px-5 py-3 text-xs text-slate-500">
        {co.source ?? '—'}
        {verified ? <div className="text-gray-400">{verified}</div> : null}
      </td>
      <td className="px-5 py-3 text-center">
        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
          {co.used_in_count}×
        </span>
      </td>
    </tr>
  );
}

function LibraryPanel({ onGoHistory, onNewRequest, industries }: LibraryPanelProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [industry, setIndustry] = useState<string>('all');
  // E06-F04-T07 — "All companies" (the whole table, default) vs "Approved only".
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const debouncedQuery = useDebounced(query);

  // Search/filter changes always reset to page 1 (T02 acceptance #1).
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, industry, approvedOnly]);

  const filter: LibraryListFilter = useMemo(
    () => ({
      q: debouncedQuery || undefined,
      industry: industry === 'all' ? undefined : industry,
      page,
      ...(approvedOnly ? { approvedOnly: true } : {}),
    }),
    [debouncedQuery, industry, page, approvedOnly],
  );

  const { data, isLoading } = useResearchLibrary(filter);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const stats = data?.stats;
  const pager = pagerWindow(total, page, rows.length);

  return (
    <div id="research-panel-library" data-testid="research-panel-library">
      {/* Stat cards (T03) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-testid="library-stats">
        <StatCard
          label="Companies in library"
          value={stats ? stats.companies_in_library.toLocaleString('en-US') : '—'}
          accent="border-green-500"
        />
        <StatCard
          label="Cache hit-rate"
          value={stats ? `${stats.cache_hit_rate}%` : '—'}
          accent="border-blue-500"
        />
        <StatCard
          label="Industries covered"
          value={stats ? String(stats.industries_covered) : '—'}
          accent="border-indigo-500"
        />
        <StatCard
          label="Verified < 90 days"
          value={stats ? String(stats.verified_under_90_days) : '—'}
          accent="border-amber-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-wrap justify-between items-center gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Company Library</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Every company in the DB — reused across searches (cache). Looking for past <b>runs</b>
              ? See{' '}
              <button
                type="button"
                onClick={onGoHistory}
                className="text-blue-600 hover:underline"
                data-testid="go-history-link"
              >
                History
              </button>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={onNewRequest}
            className="px-4 py-2 bg-slate-800 text-white rounded-full text-xs font-medium hover:bg-slate-700 transition"
          >
            New Research Request
          </button>
        </div>

        {/* filter bar */}
        <div className="px-4 py-2.5 bg-slate-50 border-b border-gray-100 flex flex-wrap items-center gap-2">
          {/* E06-F04-T07 — Library mode: the whole table vs companies approved in a submitted review */}
          <div
            className="inline-flex rounded-full border border-gray-200 bg-white p-0.5 text-xs"
            role="group"
            aria-label="Library mode"
            data-testid="library-mode"
          >
            {(
              [
                ['all', 'All companies', false],
                ['approved', 'Approved only', true],
              ] as const
            ).map(([key, label, value]) => (
              <button
                key={key}
                type="button"
                onClick={() => setApprovedOnly(value)}
                aria-pressed={approvedOnly === value}
                data-testid={`library-mode-${key}`}
                title={
                  value
                    ? 'Companies approved in a submitted review'
                    : 'Every company the searches have found'
                }
                className={
                  approvedOnly === value
                    ? 'px-3 py-1 rounded-full bg-slate-800 text-white font-medium'
                    : 'px-3 py-1 rounded-full text-slate-600 hover:bg-slate-100'
                }
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[220px]">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search company, domain, sub-industry…"
              data-testid="library-search"
              className="w-full border rounded pl-3 pr-3 py-1.5 text-sm focus:border-[#D4AF37] outline-none"
            />
          </div>
          <select
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            data-testid="library-industry"
            className="border rounded px-3 py-1.5 text-sm bg-white"
          >
            <option value="all">All industries</option>
            {industries.map((ind) => (
              <option key={ind} value={ind}>
                {ind}
              </option>
            ))}
          </select>
          <span className="text-xs text-gray-400" data-testid="library-match-count">
            {total} match{total === 1 ? '' : 'es'}
          </span>
        </div>

        <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 460px)' }}>
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-xs sticky top-0">
              <tr>
                <th className="px-5 py-3">Company</th>
                <th className="px-5 py-3">Industry / Sub</th>
                <th className="px-5 py-3">Size</th>
                <th className="px-5 py-3">HQ</th>
                <th className="px-5 py-3 text-center">Founded</th>
                <th className="px-5 py-3">Source</th>
                <th
                  className="px-5 py-3 text-center"
                  title="Number of research runs that reused this company"
                >
                  Used in
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length > 0 ? (
                rows.map((co) => <LibraryRow key={co.id} co={co} />)
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-10 text-center text-sm text-gray-400"
                    data-testid="library-empty"
                  >
                    {isLoading ? 'Loading…' : 'No companies match these filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* pager: 20 / page (T02 acceptance #3) */}
        <ListPager
          pager={pager}
          onPage={setPage}
          captionTestId="library-pager-caption"
          buttonTestIdPrefix="pager"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History panel (T04 open-by-status routing + T05 clone)
// ---------------------------------------------------------------------------

interface HistoryPanelProps {
  readonly onGoLibrary: () => void;
  readonly onNewRequest: () => void;
  readonly onOpen: (row: HistoryRequestRow) => void;
  readonly onClone: (row: HistoryRequestRow) => void;
  readonly cloningId: number | null;
  /** E06-F02-T11 — the search/status/page to open with (mount only). */
  readonly initial: HistoryBrowseFilter;
  /** E06-F02-T11 — reports every search/status/page change up. */
  readonly onFilterChange: (filter: HistoryBrowseFilter) => void;
}

/** The History panel's own filter state (the part of the browse state it owns). */
interface HistoryBrowseFilter {
  readonly query: string;
  readonly status: 'all' | ResearchRequestStatus;
  readonly page: number;
}

/** E06-F06-T03 — display label of each "found by" field (server sends the field key). */
const SEARCH_MATCH_LABELS: Record<ResearchSearchMatchField, string> = {
  title: 'title',
  client: 'client',
  industry: 'industry',
  subcategory: 'sub-category',
  location: 'location',
  competitor: 'competitor',
  company_type: 'company type',
  brief: 'brief',
};

/**
 * E06-F06-T03 — "found by" chips of a History search hit (e.g. «industry: SaaS»).
 * Computed on the server (`row.matches`); empty without a search → renders nothing.
 * E06-F04-T11 — a match on a pasted brief echoes the whole text, so collapsed chips
 * are clamped to 2 lines (full value in the tooltip) and only unclamp when expanded.
 */
function SearchMatchChips({
  matches,
  expanded = false,
}: {
  matches: readonly ResearchSearchMatch[] | undefined;
  expanded?: boolean;
}): JSX.Element | null {
  if (!matches || matches.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid="history-row-matches">
      {matches.map((m, i) => (
        <span
          key={`${m.field}-${i}`}
          data-testid="history-match-chip"
          data-field={m.field}
          title={`${SEARCH_MATCH_LABELS[m.field] ?? m.field}: ${m.value}`}
          // E06-F04-T10 — wrap, never truncate: a nowrap 260px chip set the
          // column's min-content width and pushed Actions out of the frame.
          className={`max-w-full whitespace-normal break-words px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800 ${
            expanded ? '' : 'line-clamp-2'
          }`}
        >
          <span className="text-amber-600">{SEARCH_MATCH_LABELS[m.field] ?? m.field}:</span>{' '}
          {m.value}
        </span>
      ))}
    </div>
  );
}

/** True when a clamped element hides content (vertical overflow). */
function isClamped(el: HTMLElement | null): boolean {
  return !!el && el.scrollHeight > el.clientHeight + 1;
}

/**
 * E06-F04-T11 — a request title is often a pasted brief, and its criteria summary
 * («142 · <whole brief in Industry> · … target») can be just as long. Collapsed, the
 * title is clamped to 3 whole lines and the summary + match chips to 1–2 lines, so
 * the row stays ~120px; the full texts live in native tooltips, and one explicit
 * "Show more" toggle (shown only when something actually overflows) unclamps all of
 * them. The toggle stops propagation so it never opens the row.
 */
function HistoryRequestCell({ row }: { row: HistoryRequestRow }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const summary = `${row.id} · ${row.criteria_summary}`;

  useLayoutEffect(() => {
    const t = titleRef.current;
    const s = summaryRef.current;
    if (!t || expanded) return undefined;
    const measure = (): void => setOverflowing(isClamped(t) || isClamped(s));
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(t);
    if (s) ro.observe(s);
    return () => ro.disconnect();
  }, [row.title, summary, expanded]);

  return (
    <>
      <div
        ref={titleRef}
        data-testid="history-row-title"
        data-expanded={expanded ? 'true' : 'false'}
        title={row.title}
        className={`font-medium text-slate-800 hover:text-[#B5952F] whitespace-pre-line break-words ${
          expanded ? '' : 'line-clamp-3'
        }`}
      >
        {row.title}
      </div>
      <div
        ref={summaryRef}
        data-testid="history-row-summary"
        title={summary}
        className={`text-xs text-gray-400 break-words ${expanded ? '' : 'line-clamp-1'}`}
      >
        {summary}
      </div>
      <SearchMatchChips matches={row.matches} expanded={expanded} />
      {overflowing || expanded ? (
        <button
          type="button"
          data-testid="history-title-toggle"
          aria-expanded={expanded}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          className="text-[11px] font-semibold text-[#B8962E] hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </>
  );
}

function HistoryRow({
  row,
  onOpen,
  onClone,
  cloning,
}: {
  row: HistoryRequestRow;
  onOpen: (row: HistoryRequestRow) => void;
  onClone: (row: HistoryRequestRow) => void;
  cloning: boolean;
}): JSX.Element {
  const pill = statusPill(row.status);
  return (
    <tr
      className="hover:bg-slate-50 transition cursor-pointer"
      data-testid="history-row"
      data-request-id={row.id}
      data-status={row.status}
      data-opens-to={openTarget(row)}
      onClick={() => onOpen(row)}
    >
      <td className="px-4 py-3 break-words">
        <HistoryRequestCell row={row} />
      </td>
      <td className="px-3 py-3 text-center font-semibold text-slate-700">{row.count}</td>
      <td className="px-3 py-3 text-xs text-slate-600">
        {row.project ?? <span className="text-gray-400 italic">Standalone</span>}
      </td>
      <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">{formatDate(row.date)}</td>
      <td className="px-3 py-3">
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${pill.className}`}
        >
          {pill.label}
        </span>
      </td>
      {/* E06-F04-T11 — Export folded into Actions and Open/Clone stacked, so the
          narrow columns stop eating the request column's width. */}
      <td className="px-3 py-3 text-right whitespace-nowrap">
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            data-testid="history-open-btn"
            title={`Opens on the ${openTarget(row)} screen`}
            onClick={(e) => {
              e.stopPropagation();
              onOpen(row);
            }}
            className="text-xs px-2.5 py-1 border border-slate-200 rounded text-slate-600 hover:bg-slate-100"
          >
            {openButtonCaption(row)}
          </button>
          <div className="flex items-center gap-2">
            {row.sheet_url ? (
              <a
                href={row.sheet_url}
                target="_blank"
                rel="noopener"
                data-testid="history-sheet-link"
                title="Exported Google Sheet"
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-green-700 hover:underline"
              >
                Sheet
              </a>
            ) : null}
            <button
              type="button"
              data-testid="history-clone-btn"
              disabled={cloning}
              onClick={(e) => {
                e.stopPropagation();
                onClone(row);
              }}
              title="Clone criteria into a new request"
              className="text-xs px-2.5 py-1 border border-slate-200 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              {cloning ? 'Cloning…' : 'Clone'}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

/**
 * Status dropdown options (E06-F05-T11): "All statuses" + every lifecycle value,
 * labelled by the same {@link statusPill} map the row badges use so the filter
 * and the badge can never disagree.
 */
const HISTORY_STATUS_OPTIONS: ReadonlyArray<{ value: ResearchRequestStatus; label: string }> =
  RESEARCH_REQUEST_STATUSES.map((value) => ({ value, label: statusPill(value).label }));

function HistoryPanel({
  onGoLibrary,
  onNewRequest,
  onOpen,
  onClone,
  cloningId,
  initial,
  onFilterChange,
}: HistoryPanelProps): JSX.Element {
  const [query, setQuery] = useState(initial.query);
  const [status, setStatus] = useState<'all' | ResearchRequestStatus>(initial.status);
  const [page, setPage] = useState(initial.page);
  const debouncedQuery = useDebounced(query);

  // Search/filter changes always reset to page 1 (same rule as the Library panel).
  // E06-F02-T11: only on a REAL change — the panel now remounts with a restored
  // search/status/page, and an unconditional mount-time reset would throw the
  // restored page away.
  const lastFilter = useRef({ q: debouncedQuery, status });
  useEffect(() => {
    const prev = lastFilter.current;
    if (prev.q === debouncedQuery && prev.status === status) return;
    lastFilter.current = { q: debouncedQuery, status };
    setPage(1);
  }, [debouncedQuery, status]);

  // E06-F02-T11: report the filter up so it survives this panel unmounting.
  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;
  useEffect(() => {
    onFilterChangeRef.current({ query, status, page });
  }, [query, status, page]);

  const filter: HistoryListFilter = useMemo(
    () => ({
      q: debouncedQuery || undefined,
      status: status === 'all' ? undefined : status,
      page,
    }),
    [debouncedQuery, status, page],
  );

  const { data, isLoading } = useResearchHistory(filter);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pager = pagerWindow(total, page, rows.length);

  return (
    <div id="research-panel-history" data-testid="research-panel-history">
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Research History</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Past runs · click a row to open it where it stands: <b>Request</b> if draft,{' '}
              <b>Run</b> if in progress, <b>Review</b> if done. Back to the{' '}
              <button
                type="button"
                onClick={onGoLibrary}
                className="text-blue-600 hover:underline"
                data-testid="go-library-link"
              >
                Library
              </button>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={onNewRequest}
            className="px-4 py-2 bg-slate-800 text-white rounded-full text-xs font-medium hover:bg-slate-700 transition"
          >
            New Research Request
          </button>
        </div>

        {/* filter bar (E06-F05-T11) — criteria search (E06-F06-T03) + status, both server-side */}
        <div className="px-4 py-2.5 bg-slate-50 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, industry, location…"
              data-testid="history-search"
              aria-label="Search research history by title, industry, location"
              className="w-full border rounded pl-3 pr-3 py-1.5 text-sm focus:border-[#D4AF37] outline-none"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'all' | ResearchRequestStatus)}
            data-testid="history-status"
            aria-label="Filter research history by status"
            className="border rounded px-3 py-1.5 text-sm bg-white"
          >
            <option value="all">All statuses</option>
            {HISTORY_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="text-xs text-gray-400" data-testid="history-match-count">
            {total} match{total === 1 ? '' : 'es'}
          </span>
        </div>

        {/* E06-F04-T10 — the card is overflow-hidden, so a too-wide table was
            silently clipped (Actions unreachable); scroll instead of clip. */}
        <div className="overflow-x-auto" data-testid="history-table-scroll">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-xs">
              <tr>
                {/* E06-F04-T11 — the request column takes all free width; the
                    others shrink to their content (w-px); Export lives in Actions. */}
                <th className="px-4 py-3 w-full min-w-[16rem]" data-testid="history-col-request">
                  Request / Criteria
                </th>
                <th className="px-3 py-3 text-center w-px whitespace-nowrap">Companies</th>
                <th className="px-3 py-3 w-px min-w-[6rem]">Linked project</th>
                <th className="px-3 py-3 w-px whitespace-nowrap">Date</th>
                <th className="px-3 py-3 w-px whitespace-nowrap">Status</th>
                <th className="px-3 py-3 text-right w-px whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length > 0 ? (
                rows.map((row) => (
                  <HistoryRow
                    key={row.id}
                    row={row}
                    onOpen={onOpen}
                    onClone={onClone}
                    cloning={cloningId === row.id}
                  />
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-10 text-center text-sm text-gray-400"
                    data-testid="history-empty"
                  >
                    {isLoading
                      ? 'Loading…'
                      : status !== 'all' || debouncedQuery
                        ? 'No research runs match these filters.'
                        : 'No research runs yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* pager: 20 / page (E06-F05-T11 — page 1 used to be hardcoded, so every
            run older than the newest 20 was unreachable, incl. the only
            running/failed ones) */}
        <ListPager
          pager={pager}
          onPage={setPage}
          captionTestId="history-pager-caption"
          buttonTestIdPrefix="history-pager"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// root view (T03 split-view shell)
// ---------------------------------------------------------------------------

export function ResearchLibraryView({
  onOpenScreen,
  industries,
  initialBrowse,
  onBrowseChange,
}: ResearchLibraryViewProps): JSX.Element {
  // E06-F02-T11 — panel + History filter live in one browse snapshot seeded by the
  // host and reported back to it on every change.
  const [browse, setBrowse] = useState<ResearchBrowseState>(initialBrowse ?? DEFAULT_BROWSE_STATE);
  const panel = browse.panel;
  const setPanel = (next: ResearchPanel): void =>
    setBrowse((b) => (b.panel === next ? b : { ...b, panel: next }));
  const onBrowseChangeRef = useRef(onBrowseChange);
  onBrowseChangeRef.current = onBrowseChange;
  useEffect(() => {
    onBrowseChangeRef.current?.(browse);
  }, [browse]);
  function handleHistoryFilter(f: HistoryBrowseFilter): void {
    setBrowse((b) => {
      const next: ResearchBrowseState = {
        ...b,
        historyQuery: f.query,
        historyStatus: f.status,
        historyPage: f.page,
      };
      return sameBrowseState(b, next) ? b : next;
    });
  }
  // Industry filter source: the live research dictionary (same query as the
  // Intake autocomplete / Settings card) unless the host passes an explicit list.
  const dictionaries = useResearchDictionaries();
  const industryOptions = useMemo(
    () => industryFilterOptions(industries, dictionaries.data?.industry),
    [industries, dictionaries.data],
  );
  const clone = useCloneResearchRequest();

  // T04: open a History row on the screen matching its status (target is
  // server-pre-computed in `row.opens_to`; the model re-derives as a fallback).
  function handleOpen(row: HistoryRequestRow): void {
    const screen = openTarget(row);
    onOpenScreen?.(screen, { requestId: row.id, status: row.status });
  }

  // T05 / E12-F17-T04: copy the run's criteria (read-only — no row is persisted)
  // and open Intake as a NEW request prefilled with them; Run / Save draft then
  // creates exactly one request.
  function handleClone(row: HistoryRequestRow): void {
    clone.mutate(row.id, {
      onSuccess: (res) => {
        onOpenScreen?.('intake', {
          requestId: 0,
          criteria: res.prefill.criteria,
          title: res.prefill.title,
          sourceRequestId: res.prefill.sourceRequestId,
        });
      },
    });
  }

  // The flow steps / "New Request" open Intake with a fresh (no-criteria) context.
  function handleNewRequest(): void {
    onOpenScreen?.('intake', { requestId: 0 });
  }

  return (
    <div data-testid="research-library-view">
      <ResearchSubnav panel={panel} onPanel={setPanel} />
      {panel === 'library' ? (
        <LibraryPanel
          onGoHistory={() => setPanel('history')}
          onNewRequest={handleNewRequest}
          industries={industryOptions}
        />
      ) : (
        <HistoryPanel
          onGoLibrary={() => setPanel('library')}
          onNewRequest={handleNewRequest}
          onOpen={handleOpen}
          onClone={handleClone}
          cloningId={clone.isPending ? (clone.variables ?? null) : null}
          initial={{
            query: browse.historyQuery,
            status: browse.historyStatus,
            page: browse.historyPage,
          }}
          onFilterChange={handleHistoryFilter}
        />
      )}
    </div>
  );
}

export default ResearchLibraryView;
